/**
 * Session Management Service
 *
 * Handles user session timeout, activity tracking, and auto-logout.
 * Works alongside AuthService to provide session lifecycle management.
 *
 * Features:
 * - Activity-based session timeout
 * - Warning before auto-logout
 * - Session extension on user activity
 */
import { Injectable, inject, signal, computed, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { environment } from '../../../environments/environment';

// Session configuration (can be moved to environment)
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout
const ACTIVITY_THROTTLE_MS = 60 * 1000; // Only update activity every 60 seconds

@Injectable({
    providedIn: 'root',
})
export class SessionService implements OnDestroy {
    private authService = inject(AuthService);
    private router = inject(Router);
    private ngZone = inject(NgZone);

    // Session state
    private _lastActivityTime = signal<number>(Date.now());
    private _showTimeoutWarning = signal<boolean>(false);
    private _sessionActive = signal<boolean>(false);

    // Timers
    private timeoutCheckInterval: ReturnType<typeof setInterval> | null = null;
    private activityThrottleTimeout: ReturnType<typeof setTimeout> | null = null;

    // Public signals
    readonly showTimeoutWarning = this._showTimeoutWarning.asReadonly();
    readonly lastActivityTime = this._lastActivityTime.asReadonly();
    readonly sessionActive = this._sessionActive.asReadonly();

    // Computed: time remaining until timeout
    readonly timeUntilTimeout = computed(() => {
        const elapsed = Date.now() - this._lastActivityTime();
        return Math.max(0, SESSION_TIMEOUT_MS - elapsed);
    });

    // Computed: minutes remaining (for display)
    readonly minutesRemaining = computed(() => {
        return Math.ceil(this.timeUntilTimeout() / 60000);
    });

    // Activity event listeners (bound for cleanup)
    private boundOnActivity = this.onUserActivity.bind(this);

    constructor() {
        // Start session monitoring when user is authenticated
        // This is triggered by AuthService state changes
    }

    ngOnDestroy(): void {
        this.stopSessionMonitoring();
    }

    /**
     * Start monitoring user session for timeout
     * Call this when user logs in
     */
    startSessionMonitoring(): void {
        if (this._sessionActive()) {
            return; // Already monitoring
        }

        console.log('[Session] Starting session monitoring');
        this._sessionActive.set(true);
        this._lastActivityTime.set(Date.now());
        this._showTimeoutWarning.set(false);

        // Add activity listeners (run outside Angular zone for performance)
        this.ngZone.runOutsideAngular(() => {
            window.addEventListener('mousemove', this.boundOnActivity, { passive: true });
            window.addEventListener('keydown', this.boundOnActivity, { passive: true });
            window.addEventListener('click', this.boundOnActivity, { passive: true });
            window.addEventListener('scroll', this.boundOnActivity, { passive: true });
            window.addEventListener('touchstart', this.boundOnActivity, { passive: true });
        });

        // Start timeout check interval (every 30 seconds)
        this.timeoutCheckInterval = setInterval(() => {
            this.checkSessionTimeout();
        }, 30000);
    }

    /**
     * Stop session monitoring
     * Call this when user logs out
     */
    stopSessionMonitoring(): void {
        console.log('[Session] Stopping session monitoring');
        this._sessionActive.set(false);
        this._showTimeoutWarning.set(false);

        // Remove activity listeners
        window.removeEventListener('mousemove', this.boundOnActivity);
        window.removeEventListener('keydown', this.boundOnActivity);
        window.removeEventListener('click', this.boundOnActivity);
        window.removeEventListener('scroll', this.boundOnActivity);
        window.removeEventListener('touchstart', this.boundOnActivity);

        // Clear timers
        if (this.timeoutCheckInterval) {
            clearInterval(this.timeoutCheckInterval);
            this.timeoutCheckInterval = null;
        }
        if (this.activityThrottleTimeout) {
            clearTimeout(this.activityThrottleTimeout);
            this.activityThrottleTimeout = null;
        }
    }

    /**
     * Extend the current session (called when user clicks "Stay logged in")
     */
    extendSession(): void {
        console.log('[Session] Session extended by user');
        this._lastActivityTime.set(Date.now());
        this._showTimeoutWarning.set(false);
    }

    /**
     * Force logout due to session timeout
     */
    async forceLogout(): Promise<void> {
        console.log('[Session] Session timed out - logging out');
        this.stopSessionMonitoring();
        await this.authService.logout();
        // AuthService.logout() already navigates to /login
    }

    /**
     * Handle user activity (throttled)
     */
    private onUserActivity(): void {
        // Throttle activity updates to reduce signal updates
        if (this.activityThrottleTimeout) {
            return;
        }

        this.activityThrottleTimeout = setTimeout(() => {
            this.activityThrottleTimeout = null;
        }, ACTIVITY_THROTTLE_MS);

        // Update activity time (inside Angular zone for change detection)
        this.ngZone.run(() => {
            this._lastActivityTime.set(Date.now());
            // If warning was showing, hide it since user is active
            if (this._showTimeoutWarning()) {
                this._showTimeoutWarning.set(false);
            }
        });
    }

    /**
     * Check if session has timed out or needs warning
     */
    private checkSessionTimeout(): void {
        if (!this._sessionActive()) {
            return;
        }

        const elapsed = Date.now() - this._lastActivityTime();
        const timeUntilTimeout = SESSION_TIMEOUT_MS - elapsed;

        // Session expired
        if (timeUntilTimeout <= 0) {
            this.ngZone.run(() => {
                this.forceLogout();
            });
            return;
        }

        // Show warning if approaching timeout
        if (timeUntilTimeout <= WARNING_BEFORE_TIMEOUT_MS && !this._showTimeoutWarning()) {
            this.ngZone.run(() => {
                console.log('[Session] Showing timeout warning');
                this._showTimeoutWarning.set(true);
            });
        }
    }
}
